/**
 * The pure side of the task table: verb groups in order, row order inside
 * a group, the tools no task uses, which chips to mark as unused, and the
 * formats. Kept out of the component files so the tests read it directly.
 *
 * Wording: the code says "job", every string a person reads says "task"
 * (her call, 2026-10-20: "job" is not clear to customers).
 */

import { format, parseISO } from 'date-fns'

import type { JobMapJob, JobMapReport, JobMapTool } from '@/api/generated/model'

export const UNSORTED_LABEL = 'No task'
export const UNSORTED_NOTE = 'Runs that matched no task. Each row says why.'
export const NO_TASK_TOOLS_LABEL = 'Tools not in a task'
export const NO_TASK_TOOLS_NOTE = 'Tools the agent used or could use that no task includes.'
export const BROKEN_TOOLS_NOTE = 'Tool calls written wrong. Nothing ran.'

/** The one-sentence tooltip behind each column header's info icon. */
export const COLUMN_NOTES = {
  Tools: 'Tools the agent uses for this task.',
  Share: 'Part of all runs that did this task.',
  Runs: 'Number of runs that did this task.',
  Flags: 'Problems the nightly check found in these runs.',
} as const

export interface JobGroup {
  label: string
  /** One sentence from the team's map; null when the map has none. */
  description: string | null
  jobs: JobMapJob[]
  /** Group share as the API gives it; null for the unsorted group. */
  share: number | null
}

/** Groups in verb order, each sorted by runs then map order. */
export function groupJobs(report: JobMapReport): JobGroup[] {
  const groups = report.verbs.map((verb) => ({
    label: capitalize(verb.verb),
    description: verb.description,
    share: verb.share,
    jobs: report.jobs
      .map((job, index) => ({ job, index }))
      .filter(({ job }) => job.verb === verb.verb)
      .sort((a, b) => b.job.runs - a.job.runs || a.index - b.index)
      .map(({ job }) => job),
  }))
  return [
    ...groups,
    { label: UNSORTED_LABEL, description: UNSORTED_NOTE, share: null, jobs: report.unsorted },
  ]
}

/** Tools the agent called or was offered in the window that no task uses. */
export function toolsInNoTask(report: JobMapReport): JobMapTool[] {
  return report.tools.filter((tool) => !tool.in_map && !tool.broken)
}

/** The one row summing the calls the agent wrote wrong; null when none. */
export function brokenToolRow(report: JobMapReport): JobMapTool | null {
  return report.tools.find((tool) => tool.broken && tool.runs > 0) ?? null
}

/**
 * Whether the "Not called" tag may show: once
 * the window holds enough counted runs (the API's rule, marks_ready). A
 * window with few runs says little about what was not used.
 */
export function marksUnused(report: JobMapReport): boolean {
  return report.marks_ready
}

/** The map's tools with no call in the window. Empty unless the tag may show. */
export function unusedToolNames(report: JobMapReport): Set<string> {
  return marksUnused(report) ? new Set(report.unused_tools) : new Set()
}

/**
 * Whether a task row gets the "Not called" tag: none of its tools was
 * called in the window. A task's run count is the runs it was the main
 * task of, so a task with no runs can still have its tool called inside
 * another task's run, or in runs sorted before the task was added; the
 * tag reads off tool calls, which the rows keep by name. The unsorted
 * rows have no tools, so they never get it.
 */
export function isIdleJob(job: JobMapJob, unused: Set<string>): boolean {
  return job.tools.length > 0 && job.tools.every((tool) => unused.has(tool))
}

/**
 * The one line of counts left of the window buttons: tasks, tools, and the
 * runs with the days they cover, or that counting has not started.
 */
export function countLine(report: JobMapReport): string {
  const counts = `${pluralize(report.jobs_total, 'task')} · ${pluralize(report.tools_total, 'tool')}`
  if (report.effective_since === null) return `${counts} · counting starts with the next review scan`
  const runs = pluralize(report.runs_counted, 'run')
  if (report.window_full) return `${counts} · ${runs} in the last ${String(report.window_days)} days`
  return `${counts} · ${runs} since ${formatCountedSince(report.effective_since)}`
}

/**
 * What the tag means: the counting covers the whole window, or only the days
 * since it began. Every tag's tooltip says it.
 */
export function unusedNote(report: JobMapReport): string {
  if (report.window_full || report.effective_since === null) {
    return `None of its tools used in the last ${String(report.window_days)} days.`
  }
  return `None of its tools used since ${formatCountedSince(report.effective_since)}.`
}

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1)
}

/** `0%` for none, `<0.1%` for a trace, one decimal under one percent, else whole. */
export function formatShare(share: number): string {
  const percent = share * 100
  if (percent === 0) return '0%'
  if (percent < 0.1) return '<0.1%'
  if (percent < 1) return `${percent.toFixed(1)}%`
  return `${String(Math.round(percent))}%`
}

/** `7855` as `7,855`. */
export function formatCount(count: number): string {
  return count.toLocaleString('en-US')
}

/** `2026-10-01` as `Oct 1`, for the header line. */
export function formatCountedSince(day: string): string {
  return format(parseISO(day), 'MMM d')
}

export function pluralize(count: number, noun: string): string {
  return `${formatCount(count)} ${noun}${count === 1 ? '' : 's'}`
}
